import { ApiResponse } from "src/vzbl-api-handler";
import { Field, SearchState, SearchWindow } from "src/search/types";
import getEntitiesRoute from "../api/[orgId]/graph-search/entities.route";
import getGroupBysRoute from "../api/[orgId]/graph-search/group-bys.route";
import getFacetOptionsRoute from "../api/[orgId]/graph-search/facet-options.route";
import getTimeSeriesRoute from "../api/[orgId]/graph-search/time-series.route";
import getOverviewHighlightsRoute from "../api/[orgId]/graph-search/overview-highlights.route";
import getResultSummaryRoute from "../api/[orgId]/graph-search/result-summary.route";
import getResultSummaryBulkRoute from "../api/[orgId]/graph-search/result-summary-bulk.route";
import getConfigurationRoute from "../api/[orgId]/graph-search/configuration.route";
import { post } from "./lib";
import { Api } from "@vzbl/integrations";
import { PrefixGlobalKey, readNarrativePrefix } from "./narrative-prefix";

export const groupEvents = (
  queryState: Omit<SearchState, "orgId">,
  orgId: string,
  window?: SearchWindow
) =>
  post<typeof getGroupBysRoute>(
    `/api/${orgId}/graph-search/group-bys`,
    stateWithTz({ ...queryState, ...validWindow(window) }, orgId)
  );

export const getFacetOptions = (
  queryState: Omit<SearchState, "orgId">,
  facetValuesFor: Field,
  orgId: string
) =>
  post<typeof getFacetOptionsRoute>(
    `/api/${orgId}/graph-search/facet-options`,
    stateWithTz({ ...queryState, facetValuesFor }, orgId)
  );

export const getTimeSeries = (
  queryState: Omit<SearchState, "orgId">,
  timeUnit: string,
  orgId: string
) =>
  post<typeof getTimeSeriesRoute>(
    `/api/${orgId}/graph-search/time-series`,
    stateWithTz({ ...queryState, timeUnit }, orgId)
  );

export const getEntities = (
  queryState: Omit<SearchState, "orgId">,
  page: number,
  pageSize: number,
  orgId: string,
  window: SearchWindow = {},
  signal?: AbortSignal,
  limit?: number
) =>
  post<typeof getEntitiesRoute>(
    `/api/${orgId}/graph-search/entities`,
    stateWithTz(
      { page, pageSize, limit, ...queryState, ...validWindow(window) },
      orgId
    ),
    { signal }
  );

export const getOverviewHighlights = (
  queryState: Omit<SearchState, "orgId"> & { reporter: string },
  orgId: string
) =>
  post<typeof getOverviewHighlightsRoute>(
    `/api/${orgId}/graph-search/overview-highlights`,
    stateWithTz(queryState, orgId)
  );

export const getResultSummaries = (
  queryState: Omit<SearchState, "orgId"> & {
    reporter: string;
    prompt?: string;
  },
  orgId: string
) =>
  post<typeof getResultSummaryRoute>(
    `/api/${orgId}/graph-search/result-summary`,
    stateWithTz(queryState, orgId)
  );

export const getResultSummariesBulk = async (
  sections: Api.Schema.DigestPrompts,
  orgId: string,
  globalKey: PrefixGlobalKey
) => {
  //get the global prefix and decorate
  const response = await readNarrativePrefix(globalKey);
  const prefix =
    response.length > 0
      ? `${response.map((prefix) => prefix.prefix).join("\n\n")}\n\n`
      : "";

  return post<typeof getResultSummaryBulkRoute>(
    `/api/${orgId}/graph-search/result-summary-bulk`,
    {
      sections: sections.sections.map((s) => ({
        ...s,
        prompt: prefix + s.prompt,
        queries: s.queries.map((s) => ({ ...stateWithTz(s, orgId), orgId })),
      })),
    }
  );
};

export const getGraphConfiguration = (
  orgId: string
): Promise<ApiResponse<typeof getConfigurationRoute>> =>
  post<typeof getConfigurationRoute>(
    `/api/${orgId}/graph-search/configuration`
  );

const stateWithTz = (state: any, orgId: string) => {
  if (!orgId) throw new Error("missing org id");
  return {
    ...state,
    orgId: "", // no harm to include it but makes logs easier to understand when omitted
    timezone:
      state.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

const validWindow = (window: SearchWindow = {}): SearchWindow => {
  const { limit, order, columns, timezone } = window;
  const validOrder = (order || []).filter(
    ({ by, direction }) => by && direction
  );
  const ret = { limit, order: validOrder, columns, timezone };
  if (columns === undefined) {
    delete ret.columns; // columns could be specified by searchstate. don't overwrite it.
  }
  return ret;
};
